import { type Cookies, error, redirect } from '@sveltejs/kit'
import { get } from 'svelte/store'

import { browser } from '$app/environment'
import environment from '$lib/environment'
import { sentryError, sentryMessage } from '$lib/functions/sentry'
import { account } from '$lib/stores/account/account'
import { CustomErrorMgr } from '$lib/types/custom_error'

// from browser
export async function apiGetJson(
	path: string,
	params: Record<string, string> = {},
	signal?: AbortSignal
) {
	if (!browser) return

	// https://javascript.info/url
	const url = new URL(environment.apiUrl)
	url.pathname = path
	url.search = new URLSearchParams(params).toString()
	const fetchUrl = url.pathname + url.search + url.hash

	const jsonData = await apiFetchJson(fetch, 'get', fetchUrl, undefined, undefined, signal)

	return jsonData
}

// https://developer.mozilla.org/en-US/docs/Web/API/AbortController
// const controller = new AbortController();
// const { signal } = controller;

// from browser
export async function apiPostJson(path: string, params: object = {}, signal?: AbortSignal) {
	if (!browser) return

	const body = JSON.stringify(params)

	const jsonData = await apiFetchJson(fetch, 'post', path, undefined, body, signal)

	return jsonData
}

export async function apiPostResponse(path: string, params: object = {}, signal?: AbortSignal) {
	if (!browser) return

	const body = JSON.stringify(params)

	const response = await apiFetchResponse(fetch, 'post', path, undefined, body, signal)

	return response
}

// ======================================================================
// OLD the function that call this function can call apiFetchJson instead
// 	like does ExpenseList.svelte in line 216
export async function apiPostFormRaw(
	path: string,
	body?: BodyInit,
	signal?: AbortSignal
) {
	if (!browser) return

	const url = new URL(environment.apiUrl + path)

	// Send WebAccID
	const accountID = get(account).id
	const paramsWithAccID = { webaccid: accountID }
	url.search = new URLSearchParams(paramsWithAccID).toString()

	const options: RequestInit = {
		method: 'POST',
		credentials: 'include'
	}

	if (!signal) {
		const controller = new AbortController()
		signal = controller.signal
	}
	options.signal = signal

	if (body) options.body = body

	let response: Promise<Response>
	try {
		response = fetch(url, options)
	} catch (e: unknown) {
		if (e instanceof Error) {
			if (e.name === 'AbortError' || 
				e.message === 'Load failed' ||
				e.message === 'Failed to fetch' ||
			    e.message === 'The user aborted a request' ||
				e.message === 'The operation was aborted.' || 
				e.message === 'NetworkError when attempting to fetch resource.') {
				return null // catch the abort()
			}
			const error = new Error(e.toString())
			sentryError(error.message, { url: url.toString(), ...options })
		}
		throw e
	}

	return response
}

// ==================================
// function receiving fetch from caller
export async function apiFetchJson(
	pFetch: TypeFetch,
	fetchMethod: string,
	fetchUrl: string,
	cookies?: CookieItem[],
	body?: BodyInit,
	signal?: AbortSignal
) {
	const response: Response | null = await apiFetchResponse(
		pFetch,
		fetchMethod,
		fetchUrl,
		cookies,
		body,
		signal
	)
	if (response === null) return null

	const jsonData = await response.json()


	if (jsonData?.error) {
		// move the following code to the CustomErrorMgr
		if (CustomErrorMgr.IsWarning(jsonData.error))
			console.warn('-> CustomWarning ' + JSON.stringify(jsonData.error, null, 2))
		if (CustomErrorMgr.IsError(jsonData.error))
			console.error('-> CustomError ' + JSON.stringify(jsonData.error, null, 2))

		if (CustomErrorMgr.HasErrorInfo(jsonData.error) && CustomErrorMgr.IsUnknowError(jsonData.error)) {
			sentryError(jsonData.error.message)
		}
	}

	
	return jsonData
}

export async function apiFetchResponse(
	pFetch: TypeFetch,
	fetchMethod: string,
	fetchUrl: string,
	cookies?: CookieItem[],
	body?: BodyInit,
	signal?: AbortSignal
) {

	const url = new URL(environment.apiUrl + fetchUrl)

	const response: Response | null = await apiFetchRaw(
		pFetch,
		fetchMethod,
		url,
		cookies,
		body,
		signal
	)

	if (response === null) return null

	if (response.status === 403) {
		const message = `Not authorized: ${url.pathname}`
		sentryMessage(message)
		console.warn('API.WARNING 403 ', message)
		if (browser)
			window.location.href = '/login'
			// goto('/login')
		else
			throw redirect(302, '/login')
	} else if (response.status === 404) {
		const message = `Not found: ${url.pathname}`
		sentryMessage(message)
		console.warn('API.WARNING 404 ', message)
		throw error(404)
	} else if (response.status >= 500) {
		// Bad request, forbidden, and not found are handled either above or in the calling code
		// only 500 errors are recorded automatically
		console.log('fetch response.status === 500')
		const err = new Error(`${url.pathname} ${response.status}: ${response.statusText}`)
		// sentryError(err.message)
		throw err
	}

	return response
}

export async function apiFetchRaw(
	pFetch: TypeFetch,
	fetchMethod: string,
	fetchUrl: URL,
	cookies?: CookieItem[],
	body?: BodyInit,
	signal?: AbortSignal
) {
	console.log('apiFetchRaw #1', fetchUrl.href)

	const options: RequestInit = { 
		method: fetchMethod, 
		credentials: 'include' 
	}

	if (!signal) {
		const controller = new AbortController()
		signal = controller.signal
	}
	options.signal = signal

	if (body) options.body = body

	let cookiesStr = ''
	if (cookies) {
		// console.log('cookies', cookies)
		cookiesStr = cookies
			.map((item: CookieItem) => {
				return `${item.name}=${item.value}`
			})
			.join(';')
		options.headers = { cookie: cookiesStr }
	}

	let response: Response
	try {
		response = await pFetch(fetchUrl, options)
	} catch (e) {
		console.warn('apiProxy - fetch - catch error options', 
					 fetchUrl.href, 
					 options)
		if (e instanceof Error) {
			if (e.name === 'AbortError' || 
				e.message === 'Load failed' ||
				e.message === 'Failed to fetch' ||
			    e.message === 'The user aborted a request' ||
				e.message === 'The operation was aborted.' || 
				e.message === 'NetworkError when attempting to fetch resource.') {
				return null // catch the abort()
			}
			// const error = new Error(e.toString())
			// sentryError(error.message, { url: fetchUrl.toString(), ...options })

			// Send WebAccID
			const webaccid = get(account).id

			const errorStr = e.toString()
			sentryError(errorStr, { url: fetchUrl.toString(), webaccid: webaccid, ...options })
		}
		throw e
	}

	return response
}

export function apiGetAppCookies(cookies: Cookies) {
	return cookies.getAll().filter((item: CookieItem) => {
		if (item.name.startsWith(environment.cookiePrefix + '-inv-')) return item
	})
}

export type CookieItem = {
	name: string
	value: string
}

export type TypeFetch = (input: RequestInfo | URL, init?: RequestInit | undefined) => Promise<Response>
